import { dayKey } from "./format";

// Logique pure de la page Discipline du Lite : les entités Prisma sont lues à
// travers des formes minimales pour rester testables sans Wasp.
// Les complétions sont stockées à minuit dans le fuseau du serveur : voir completionDay.

export interface RoutineCompletionLike { date: Date | string; completedItems: string[] }
export interface RoutineLike {
  id: string;
  name: string;
  checklistItems: string[];
  frequency: string;
  isActive: boolean;
  routineCompletions: RoutineCompletionLike[];
}
export interface GoalHistoryLike { periodEnd: Date | string; wasAchieved: boolean }
export interface GoalLike {
  id: string;
  title: string;
  type: string;
  targetValue: number;
  currentValue: number;
  deadline: Date | string | null;
  isCompleted: boolean;
  recurrence: string;
  history: GoalHistoryLike[];
}

export interface ChecklistItem { label: string; done: boolean }
export interface ChecklistGroup<R extends RoutineLike = RoutineLike> { routine: R; items: ChecklistItem[]; done: number }
export interface TodayChecklist<R extends RoutineLike = RoutineLike> { groups: ChecklistGroup<R>[]; done: number; total: number }

/**
 * Jour calendaire d'une complétion. La date est stockée à minuit dans le fuseau du
 * SERVEUR (UTC en prod) : +12 h puis lecture en UTC retrouve ce jour quel que soit
 * le fuseau du navigateur (valable pour tout décalage serveur entre -12 h et +12 h).
 */
const completionDay = (date: Date | string) => {
  const d = new Date(new Date(date).getTime() + 12 * 3_600_000);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
};

/**
 * Date à envoyer au serveur pour une complétion « aujourd'hui » : midi local. Le serveur
 * la ramène à minuit dans SON fuseau ; midi local tombe sur le même jour calendaire pour
 * tout décalage entre -12 h et +12 h (à 00 h 30 à Paris, `new Date()` donnerait la veille en UTC).
 */
export function completionDateFor(now = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
}

const completionOn = (r: RoutineLike, day: string) => r.routineCompletions.find(c => completionDay(c.date) === day);
const checkedOn = (r: RoutineLike, day: string) => new Set(completionOn(r, day)?.completedItems ?? []);

/** Items de toutes les routines actives, avec l'état coché du jour. */
export function todayChecklist<R extends RoutineLike>(routines: R[], now = new Date()): TodayChecklist<R> {
  const day = dayKey(now);
  const groups = routines.filter(r => r.isActive).map(r => {
    const checked = checkedOn(r, day);
    const items = r.checklistItems.map(label => ({ label, done: checked.has(label) }));
    return { routine: r, items, done: items.filter(i => i.done).length };
  });
  return {
    groups,
    done: groups.reduce((a, g) => a + g.done, 0),
    total: groups.reduce((a, g) => a + g.items.length, 0),
  };
}

const isFull = (r: RoutineLike, day: string) => {
  const checked = checkedOn(r, day);
  return r.checklistItems.length > 0 && r.checklistItems.every(i => checked.has(i));
};

/**
 * Jours consécutifs où TOUTES les routines quotidiennes actives étaient complètes,
 * en remontant depuis aujourd'hui, ou depuis hier si aujourd'hui n'est pas fini.
 */
export function routineStreak(routines: RoutineLike[], now = new Date()): number {
  const daily = routines.filter(r => r.isActive && r.frequency === "daily" && r.checklistItems.length > 0);
  if (!daily.length) return 0;
  const cursor = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
  const fullOn = (d: Date) => daily.every(r => isFull(r, dayKey(d)));
  if (!fullOn(cursor)) cursor.setDate(cursor.getDate() - 1);
  let streak = 0;
  // 366 : fenêtre de 12 mois de getRoutinesByUser
  while (streak < 366 && fullOn(cursor)) { streak++; cursor.setDate(cursor.getDate() - 1); }
  return streak;
}

export interface GoalProgress {
  /** currentValue / targetValue, borné à [0, 1]. */
  ratio: number;
  achieved: boolean;
  /** Jours calendaires avant l'échéance (0 = aujourd'hui), null sans échéance. */
  remainingDays: number | null;
  expired: boolean;
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

// Jours restants d'une période récurrente, calculés en LOCAL depuis `now` : le serveur écrit
// `deadline` à 23:59:59.999 dans SON fuseau (UTC en prod), ce qui peut tomber le lendemain dans
// le fuseau du navigateur. On ignore donc `deadline` pour ce calcul et on redérive de la récurrence.
function remainingDaysFromRecurrence(recurrence: string, now: Date): number | null {
  if (recurrence === "daily") return 0;
  if (recurrence === "weekly") return (7 - now.getDay()) % 7;
  if (recurrence === "monthly") {
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return lastDayOfMonth - now.getDate();
  }
  return null;
}

/** Progression d'un goal : ratio borné, atteint, jours restants, période écoulée. */
export function goalProgress(g: GoalLike, now = new Date()): GoalProgress {
  const achieved = g.type === "custom" ? g.isCompleted : g.currentValue >= g.targetValue;
  const ratio = g.targetValue > 0 ? Math.max(0, Math.min(1, g.currentValue / g.targetValue)) : achieved ? 1 : 0;
  const deadline = g.deadline ? new Date(g.deadline) : null;
  const expired = !!deadline && deadline.getTime() < now.getTime();
  const fromRecurrence = g.recurrence !== "none" ? remainingDaysFromRecurrence(g.recurrence, now) : null;
  const remainingDays = fromRecurrence !== null
    ? fromRecurrence
    : deadline ? Math.max(0, Math.round((startOfDay(deadline) - startOfDay(now)) / 86_400_000)) : null;
  return { ratio, achieved, remainingDays, expired };
}

/** En cours : récurrent dont la période court (même atteint ; une période écoulée mais pas encore réinitialisée par le cron est masquée, comme updateGoalProgress l'ignore), ou ponctuel non terminé. */
export function activeGoals<G extends GoalLike>(goals: G[], now = new Date()): G[] {
  return goals.filter(g => (g.recurrence !== "none" ? !goalProgress(g, now).expired : !g.isCompleted));
}

/** Terminés : ponctuels cochés ou atteints. Les récurrents ne se terminent jamais. */
export function completedGoals<G extends GoalLike>(goals: G[]): G[] {
  return goals.filter(g => g.recurrence === "none" && g.isCompleted);
}

/** Les n dernières périodes d'un goal récurrent, du plus ancien au plus récent. */
export function recentPeriods(g: GoalLike, n = 8): boolean[] {
  if (n <= 0) return [];
  return [...g.history]
    .sort((a, b) => new Date(a.periodEnd).getTime() - new Date(b.periodEnd).getTime())
    .slice(-n)
    .map(h => h.wasAchieved);
}

/** Cible du premier goal PnL quotidien : l'« objectif du jour » des stats de psychologie. */
export function dailyPnlGoal(goals: GoalLike[]): number | null {
  const g = goals.find(x => x.type === "pnl" && x.recurrence === "daily" && x.targetValue > 0);
  return g ? g.targetValue : null;
}

/** Goals calculés en cours, les plus proches de leur cible d'abord, les atteints en dernier (card du dashboard). */
export function closestGoals<G extends GoalLike>(goals: G[], n = 2, now = new Date()): G[] {
  return activeGoals(goals, now)
    .filter(g => g.type !== "custom")
    .sort((a, b) => {
      const pa = goalProgress(a, now), pb = goalProgress(b, now);
      return Number(pa.achieved) - Number(pb.achieved) || pb.ratio - pa.ratio;
    })
    .slice(0, n);
}
